import { useMutation, useQuery } from "@apollo/client";
import { Loading } from "@ottatech/shared-components";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Middle,
  Card as DefaultCard,
  Text,
  modularScale,
  pxToRem,
} from "@ottatech/design";
import { useLocation, useNavigate } from "react-router-dom";
import { useExperiment, pushAnalyticsEvent } from "@ottatech/analytics";
import styled from "styled-components";
import { useEffect, useState } from "react";
import * as queryString from "query-string";

import { BatchOptions } from "../JobDeck/BatchOptions";

import { RolloutModal } from "./RolloutModal";
import { JobContent } from "./JobContent";

import {
  PublicCreateJobApplicationDocument,
  PublicJobDocument,
  UserJobPreferencesDocument,
} from "schema";
import { countryToCurrency } from "utils/currencies";
import { useUserLocation } from "hooks/useUserLocation";
import { ApplyViaOttaButtonContent } from "components/ApplyViaOttaButtonContent";
import { useUtmSource } from "utils/routing/useUtmSource";
import { Separator } from "components/Separator";

const ROLLOUT_MODAL_FLAG = "JOB_CARD_V2_ROLLOUT_MODAL";

export enum JobCardType {
  jobDeck = "JOB_DECK",
  dashboard = "DASHBOARD",
  public = "PUBLIC",
  application = "APPLICATION",
}

const Card = styled(DefaultCard)`
  padding: 0;
  // So that card looks full even when loading
  min-height: 100vh;
  margin-bottom: ${modularScale(1)};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${pxToRem(24)};
`;

interface JobCardProps {
  jobExternalId: string;
  type: JobCardType;
  userExternalId?: string;
  batchDetails?: BatchOptions;
  publicJob?: boolean;
  toggleKeyboardShortcuts?: (enable: boolean) => void;
}

export function JobCard({
  jobExternalId,
  type,
  userExternalId,
  batchDetails,
  publicJob = false,
  toggleKeyboardShortcuts,
}: JobCardProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { country: userLocation } = useUserLocation();
  const currency = countryToCurrency(userLocation);

  const utmSource = useUtmSource();
  const isPublic = type === JobCardType.public;

  const [showRolloutModal, setShowRolloutModal] = useState(false);

  const { variant: flag, loading: flagLoading } =
    useExperiment(ROLLOUT_MODAL_FLAG);
  const showRollout = flag === "variant" && !flagLoading;

  const shownModalCookie = localStorage.getItem(ROLLOUT_MODAL_FLAG);
  const shownModal = shownModalCookie
    ? JSON.parse(shownModalCookie) === true
    : false;

  useEffect(() => {
    const { onboard = false } = (location.state ?? {}) as {
      onboard?: true;
    };

    const shouldShowRolloutModal =
      showRollout && !shownModal && !publicJob && !onboard;

    setShowRolloutModal(shouldShowRolloutModal);
  }, [showRollout, shownModal, publicJob, location]);

  const [publicApplicationMutation] = useMutation(
    PublicCreateJobApplicationDocument
  );

  const { data: jobData, loading: jobLoading } = useQuery(PublicJobDocument, {
    variables: {
      externalId: jobExternalId,
      currency,
      prefixImage: false,
    },
  });

  // User preferences data, skip if user not logged in
  const { data: userData, loading: userLoading } = useQuery(
    UserJobPreferencesDocument,
    { skip: isPublic }
  );

  if (jobLoading || userLoading || flagLoading) {
    return (
      <Card>
        <ContentContainer>
          <Loading />
        </ContentContainer>
      </Card>
    );
  }

  const job = jobData?.publicJob;

  if (!job) {
    return (
      <Card>
        <ContentContainer>
          <Text align="center">Job not found</Text>
        </ContentContainer>
      </Card>
    );
  }

  const handleRolloutModalClose = () => {
    localStorage.setItem(ROLLOUT_MODAL_FLAG, "true");
    setShowRolloutModal(false);
  };

  const handlePublicJobApplyClick = () => {
    if (isPublic && job.originalUrl) {
      pushAnalyticsEvent({
        eventName: "Clicked Public Apply Link",
        jobId: job.externalId,
        utmSource,
      });

      window.open(job.originalUrl);

      if (userExternalId) {
        publicApplicationMutation({
          variables: {
            jobId: job.id,
            userExternalId,
          },
        });
      }
    } else {
      navigate({
        pathname: `/apply/sign-up/${jobExternalId}`,
        search: queryString.stringify({
          utm_source: utmSource,
          redirect: `/jobs/${jobExternalId}/apply`,
        }),
      });
    }
  };

  const user = userData?.currentUser ?? undefined;

  return (
    <>
      <Helmet>
        <title>
          {job.title} - {job.company.name} | Otta
        </title>
      </Helmet>
      <ContentContainer data-testid="job-card-v2">
        <JobContent
          job={job}
          user={user}
          type={type}
          batchDetails={batchDetails}
          publicJob={publicJob}
          toggleKeyboardShortcuts={toggleKeyboardShortcuts}
        />
        {job.live && isPublic && (
          <>
            <Separator />
            <Middle>
              <Button
                data-testid="apply-button"
                type="button"
                level="primary"
                onClick={handlePublicJobApplyClick}
              >
                <ApplyViaOttaButtonContent
                  isApplyViaOtta={job.acceptsInternalApplications}
                />
              </Button>
            </Middle>
          </>
        )}
      </ContentContainer>
      {showRolloutModal && (
        <RolloutModal handleClose={handleRolloutModalClose} />
      )}
    </>
  );
}
